/**
 * Wrapper around Swiper JS
 * See https://swiperjs.com/react for props/documentation
 */

import {
  Children,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MutableRefObject,
} from 'react';
import classNames from 'classnames';
import { Autoplay, EffectFade, Navigation, A11y, Virtual } from 'swiper/modules';
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperType } from 'swiper/types';
import { CarouselTitle, CarouselTitleProps } from '@components/carousel/CarouselTitle';
import { Icon } from '@components/icon';
import { disableFocusableDescendants, restoreFocusableDescendants } from '@components/utils';

import {
  applyChevronFocusOverrides,
  isChevronActivationKey,
  useChevronTileAlign,
  useKeepChevronsFocusable,
  useOnPrevClick,
  usePeekAheadNavigation,
  useReducedMotionAutoplay,
} from './hooks';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/a11y';
import 'swiper/css/effect-fade';
import 'swiper/css/virtual';

// Import style overides
import './Carousel.scss';

const DEFAULT_SPACE_BETWEEN = 10;

const TABINDEX_BACKUP_ATTR = 'data-vds-carousel-orig-tabindex';

// Hide non-visible slides from AT (Swiper's A11y module doesn't). Prefers
// `swiper-slide-visible` (we force `watchSlidesProgress` when the prop is on),
// falls back to `swiper-slide-active` for single-slide carousels.
// Module-scope so the reference is stable across renders.
const applyInactiveSlideA11y = (swiper: SwiperType): void => {
  const usesVisibleClass = swiper.slides.some(s => s.classList.contains('swiper-slide-visible'));
  const accessibleClass = usesVisibleClass ? 'swiper-slide-visible' : 'swiper-slide-active';
  swiper.slides.forEach(slide => {
    if (slide.classList.contains(accessibleClass)) {
      slide.removeAttribute('aria-hidden');
      slide.removeAttribute('inert');
      restoreFocusableDescendants(slide, TABINDEX_BACKUP_ATTR);
    } else {
      slide.setAttribute('aria-hidden', 'true');
      slide.setAttribute('inert', '');
      disableFocusableDescendants(slide, TABINDEX_BACKUP_ATTR);
    }
  });
};

const clearInactiveSlideA11y = (swiper: SwiperType): void => {
  swiper.slides.forEach(slide => {
    slide.removeAttribute('aria-hidden');
    slide.removeAttribute('inert');
    restoreFocusableDescendants(slide, TABINDEX_BACKUP_ATTR);
  });
};

type SwiperA11yOptions = Exclude<SwiperProps['a11y'], boolean | undefined>;
type ChevronButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'>;

const getChevronAriaLabel = (buttonProps: ChevronButtonProps | undefined): string | undefined => {
  const label = buttonProps?.['aria-label'];
  return typeof label === 'string' && label.length > 0 ? label : undefined;
};

/** Mouse clicks must not shift focus onto chevrons — that fires container blur snap. */
const withChevronMouseDownGuard = (
  buttonProps: ChevronButtonProps | undefined,
): ChevronButtonProps => {
  const { onMouseDown, ...rest } = buttonProps ?? {};
  return {
    ...rest,
    onMouseDown: (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      onMouseDown?.(event);
    },
  };
};

type ChevronKeyboardDirection = 'prev' | 'next';

const resolveChevronKeyboardDirection = (
  target: EventTarget | null,
  prevEl: HTMLElement | null | undefined,
  nextEl: HTMLElement | null | undefined,
): ChevronKeyboardDirection | undefined => {
  if (!(target instanceof Node)) return undefined;
  if (prevEl instanceof HTMLButtonElement && (target === prevEl || prevEl.contains(target))) {
    return 'prev';
  }
  if (nextEl instanceof HTMLButtonElement && (target === nextEl || nextEl.contains(target))) {
    return 'next';
  }
  return undefined;
};

const beginChevronKeyboardNav = (
  swiper: SwiperType,
  chevronKeyboardNavRef: MutableRefObject<boolean>,
  activeFinishRef: MutableRefObject<(() => void) | null>,
): (() => void) => {
  activeFinishRef.current?.();

  chevronKeyboardNavRef.current = true;
  let finalized = false;

  const finishChevronKeyboardNav = (): void => {
    if (finalized) return;
    finalized = true;
    swiper.off('slideChangeTransitionEnd', finishChevronKeyboardNav);
    if (activeFinishRef.current === finishChevronKeyboardNav) {
      activeFinishRef.current = null;
    }
    // Defer until after container focusout snap (which runs in rAF) has had a chance
    // to see the guard still set.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        chevronKeyboardNavRef.current = false;
      });
    });
  };

  activeFinishRef.current = finishChevronKeyboardNav;
  swiper.on('slideChangeTransitionEnd', finishChevronKeyboardNav);
  return finishChevronKeyboardNav;
};

const isFocusWithinCarousel = (
  carouselContainer: Element | null,
  swiperEl: HTMLElement,
  relatedTarget: Node | null,
  activeElement: Element | null = null,
): boolean => {
  if (relatedTarget && swiperEl.contains(relatedTarget)) {
    return true;
  }
  if (relatedTarget instanceof Element && Boolean(carouselContainer?.contains(relatedTarget))) {
    return true;
  }
  if (activeElement && swiperEl.contains(activeElement)) {
    return true;
  }
  return activeElement instanceof Element && Boolean(carouselContainer?.contains(activeElement));
};

const snapCarouselToFirstSlideIfNeeded = (swiper: SwiperType): void => {
  if (swiper.activeIndex !== 0) {
    swiper.slideTo(0, 0);
  }
};

/** Swiper's A11y module overwrites chevron `aria-label` at init from `a11y.*SlideMessage`. */
const mergeNavigationA11y = (
  a11y: SwiperProps['a11y'],
  prevButtonProps: ChevronButtonProps | undefined,
  nextButtonProps: ChevronButtonProps | undefined,
): SwiperProps['a11y'] | undefined => {
  if (a11y === false) return false;

  const base: SwiperA11yOptions =
    a11y === true ? {} : typeof a11y === 'object' && a11y !== null ? a11y : {};

  const prevSlideMessage = base.prevSlideMessage ?? getChevronAriaLabel(prevButtonProps);
  const nextSlideMessage = base.nextSlideMessage ?? getChevronAriaLabel(nextButtonProps);

  const hasExplicitA11y = a11y !== undefined;
  const hasNavMessages = prevSlideMessage !== undefined || nextSlideMessage !== undefined;

  if (!hasExplicitA11y && !hasNavMessages) {
    return undefined;
  }

  return {
    ...base,
    ...(prevSlideMessage !== undefined ? { prevSlideMessage } : {}),
    ...(nextSlideMessage !== undefined ? { nextSlideMessage } : {}),
  };
};

export interface CarouselProps
  extends SwiperProps,
    Pick<CarouselTitleProps, 'numberShows' | 'isLive' | 'href'> {
  id: string;
  title?: string;
  /** Custom component to render the title link instead of a native `<a>` (e.g. Next.js Link). Must accept `href` as a prop. */
  titleLinkComponent?: CarouselTitleProps['linkComponent'];
  /** Optional action link (e.g. "Edit") rendered on the top-right of the title row. Requires `title`; not rendered when `isTitleHidden` is true. */
  action?: CarouselTitleProps['action'];
  /** Hide the title without removing the div from the DOM */
  isTitleHidden?: boolean;
  titleDataCi?: string;
  /** Carousel variant for different use cases */
  variant?: 'default' | 'peek-ahead';
  /**
   * For `variant="peek-ahead"`: shows the left chevron (hidden by default).
   * Pair with `onPrevClick` to keep it focusable and clickable when Swiper
   * would mark it disabled. No-op otherwise
   */
  showPrevButton?: boolean;
  /**
   * Runs on prev-chevron activation (mouse or keyboard). When Swiper would
   * short-circuit its own prev handler (e.g. `allowSlidePrev=false`, or
   * `isBeginning && !loop`) this acts as the sole prev handler; otherwise it
   * runs as a side-effect alongside Swiper's own slide-back
   */
  onPrevClick?: () => void;
  /**
   * Stops autoplay when `prefers-reduced-motion: reduce` is on (and starts it
   * again when cleared). Defaults to `false`
   */
  respectReducedMotion?: boolean;
  /**
   * Adds `aria-hidden` + `inert` to inactive slides so screen readers and
   * keyboard users skip them. Defaults to `false`
   */
  hideInactiveSlidesFromA11y?: boolean;
  /** Pass-through props for the left chevron button. `a11y.prevSlideMessage` takes precedence over `aria-label`. `type` is always `button` */
  prevButtonProps?: Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'>;
  /**
   * Pass-through props for the right chevron button. `a11y.nextSlideMessage` takes precedence over
   * `aria-label`. `type` is always `button`. `onClick` does not run when `variant="peek-ahead"` and
   * `allowSlideNext={false}` — use `onSwiper` + `navigationNext` instead
   */
  nextButtonProps?: Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'>;
  /**
   * Controls vertical alignment of navigation chevrons. `container` (default) centers
   * on the full slide height; `tile` centers on the first visible media element
   * (`data-vds-carousel-media`) so caption content below is excluded. Use `container`
   * for media-only slides (portrait rails, avatars) where slide height equals media height.
   */
  chevronAlign?: 'container' | 'tile';
  /**
   * Selector for the slide media element when `chevronAlign="tile"`.
   * Defaults to `[data-vds-carousel-media]`.
   */
  chevronAlignSelector?: string;
  /** When `chevronAlign="tile"`, anchor chevrons to the top or bottom of the measured tile area. Defaults to `top`. */
  chevronAlignOrigin?: 'top' | 'bottom';
}

/**
 * Carousel component
 * @param id - ID Identifying the carousel
 * @param title - Optional heading for the carousel
 * @param titleLinkComponent - Custom component to render the title link instead of a native `<a>` (e.g. Next.js Link). Must accept `href` as a prop.
 * @param action - Optional action link (e.g. "Edit") rendered on the top-right of the title row. Requires `title`; not rendered when `isTitleHidden` is true.
 * @param isTitleHidden - Hide the title without removing the div from the DOM
 * @param titleDataCi - Test ID rendered as `data-ci` on the title heading, suffixed `-small` / `-medium` per title size
 * @param variant - Carousel variant for different use cases
 * @param showPrevButton - Shows the left chevron (hidden by default). Pair with `onPrevClick` to keep it focusable and clickable when Swiper would mark it disabled. No-op otherwise
 * @param respectReducedMotion - Stops autoplay when `prefers-reduced-motion: reduce` is on (and starts it again when cleared). Defaults to `false`
 * @param hideInactiveSlidesFromA11y - Adds `aria-hidden` + `inert` to inactive slides so screen readers and keyboard users skip them. Defaults to `false`
 * @param prevButtonProps - Pass-through props for the left chevron; `a11y.prevSlideMessage` takes precedence over `aria-label`
 * @param nextButtonProps - Pass-through props for the right chevron; `a11y.nextSlideMessage` takes precedence over `aria-label`
 * @param chevronAlign - `container` centers chevrons on the full slide; `tile` centers on the visible tile image area
 * @param chevronAlignSelector - Selector for the slide media element when `chevronAlign="tile"`
 * @param chevronAlignOrigin - Anchor chevrons to the `top` or `bottom` of the measured tile area
 * @param onPrevClick - Runs on prev-chevron activation (mouse or keyboard). When Swiper would short-circuit its own prev handler (e.g. `allowSlidePrev=false`, or `isBeginning && !loop`) this acts as the sole prev handler; otherwise it runs as a side-effect alongside Swiper's own slide-back
 * @param onSwiper - function to be called on init, received Swiper instance as first arg
 * @param virtual - controls whether virtual slides are enabled. default true
 * @constructor
 */
export const Carousel: React.FC<CarouselProps> = ({
  id,
  title,
  isLive,
  numberShows,
  isTitleHidden,
  titleDataCi,
  variant = 'default',
  showPrevButton = false,
  respectReducedMotion = false,
  hideInactiveSlidesFromA11y = false,
  onPrevClick,
  children,
  onSwiper,
  onSlideChange,
  onBlur,
  className,
  virtual = true,
  href,
  titleLinkComponent,
  action,
  navigation = true,
  allowSlideNext,
  onBeforeInit,
  prevButtonProps,
  nextButtonProps,
  chevronAlign = 'container',
  chevronAlignSelector,
  chevronAlignOrigin = 'top',
  a11y,
  ...swiperRest
}: CarouselProps) => {
  const { onAfterInit: consumerOnAfterInit, ...restSwiperProps } = swiperRest;
  const { className: prevButtonClassName, ...prevButtonRest } =
    withChevronMouseDownGuard(prevButtonProps);
  const { className: nextButtonClassName, ...nextButtonRest } =
    withChevronMouseDownGuard(nextButtonProps);
  const mergedA11y = mergeNavigationA11y(a11y, prevButtonProps, nextButtonProps);
  const swiperRef = useRef<SwiperType | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const chevronKeyboardNavRef = useRef(false);
  const chevronNavFinishRef = useRef<(() => void) | null>(null);
  const hasCarouselChangedSlidesRef = useRef(false);
  const [isSwiperReady, setIsSwiperReady] = useState(false);
  const [isNavReady, setIsNavReady] = useState(false);
  const [isNavInactive, setIsNavInactive] = useState(true);
  const navigationEnabled = navigation !== false;

  const handleBeforeInit: NonNullable<SwiperProps['onBeforeInit']> = swiper => {
    if (
      navigationEnabled &&
      swiper.params.navigation &&
      typeof swiper.params.navigation === 'object'
    ) {
      swiper.params.navigation.prevEl = prevRef.current;
      swiper.params.navigation.nextEl = nextRef.current;
    }
    onBeforeInit?.(swiper);
  };

  const handleOnSwiper: (swiper: SwiperType) => void = swiper => {
    swiperRef.current = swiper;
    setIsSwiperReady(true);
    if (onSwiper) {
      onSwiper(swiper);
    }
  };

  const handleAfterInit: NonNullable<SwiperProps['onAfterInit']> = swiper => {
    consumerOnAfterInit?.(swiper);
  };

  useEffect(() => {
    if (!isSwiperReady) return;
    const swiper = swiperRef.current;
    if (!swiper) return;

    let cancelled = false;

    const syncNavEdges = (): void => {
      if (cancelled) return;
      swiper.navigation?.update?.();
      setIsNavInactive(swiper.isBeginning && swiper.isEnd);
    };

    const finalizeInit = (): void => {
      if (cancelled) return;
      swiper.update();
      if (hideInactiveSlidesFromA11y) {
        applyInactiveSlideA11y(swiper);
      }
      if (navigationEnabled) {
        syncNavEdges();
        setIsNavReady(true);
      }
    };

    // Defer until layout has settled (images, virtual slides, etc.) so Swiper
    // applies slide visibility classes before a11y hiding runs.
    requestAnimationFrame(() => {
      requestAnimationFrame(finalizeInit);
    });

    if (navigationEnabled) {
      swiper.on('resize', syncNavEdges);
    }

    return () => {
      cancelled = true;
      if (navigationEnabled) {
        swiper.off('resize', syncNavEdges);
      }
    };
  }, [navigationEnabled, isSwiperReady]);

  const handleSlideChange: NonNullable<SwiperProps['onSlideChange']> = swiper => {
    hasCarouselChangedSlidesRef.current = true;

    if (navigationEnabled) {
      swiper.navigation?.update?.();
      setIsNavInactive(swiper.isBeginning && swiper.isEnd);
    }
    if (hideInactiveSlidesFromA11y) {
      applyInactiveSlideA11y(swiper);
    }
    if (onSlideChange) {
      onSlideChange(swiper);
    }
  };

  // Re-enabling `navigation` hands Swiper null refs (chevrons mount after render), so it never re-inits.
  useLayoutEffect(() => {
    if (!navigationEnabled || !isSwiperReady) return;
    const swiper = swiperRef.current;
    const navigationParams = swiper?.params.navigation;
    if (!swiper?.navigation || !navigationParams || typeof navigationParams !== 'object') return;
    if (
      swiper.navigation.prevEl === prevRef.current &&
      swiper.navigation.nextEl === nextRef.current
    ) {
      return;
    }
    navigationParams.prevEl = prevRef.current;
    navigationParams.nextEl = nextRef.current;
    swiper.navigation.init();
    swiper.navigation.update();

    // Swiper's A11y module only labels chevrons on its own init.
    const a11yParams = swiper.params.a11y;
    if (a11yParams && typeof a11yParams === 'object' && a11yParams.enabled !== false) {
      if (a11yParams.prevSlideMessage) {
        prevRef.current?.setAttribute('aria-label', a11yParams.prevSlideMessage);
      }
      if (a11yParams.nextSlideMessage) {
        nextRef.current?.setAttribute('aria-label', a11yParams.nextSlideMessage);
      }
    }
  }, [navigationEnabled, isSwiperReady]);

  // React re-renders clear Swiper's boundary `disabled` attrs; re-sync before paint.
  useLayoutEffect(() => {
    if (!navigationEnabled || !isNavReady) return;
    swiperRef.current?.navigation?.update?.();
  }, [navigationEnabled, isNavReady, isNavInactive]);

  useReducedMotionAutoplay({
    swiperRef,
    respectReducedMotion,
    isSwiperReady,
  });

  const hasOnPrevClick = useOnPrevClick({
    swiperRef,
    navigationEnabled,
    isSwiperReady,
    onPrevClick,
  });

  useChevronTileAlign({
    enabled: chevronAlign === 'tile' && navigationEnabled,
    containerRef,
    swiperRef,
    isSwiperReady,
    selector: chevronAlignSelector,
  });

  // Peek-ahead + onPrevClick: always keep prev interactive at isBeginning
  // (see useKeepChevronsFocusable for focus-gated default behavior).
  useEffect(() => {
    if (
      !navigationEnabled ||
      variant !== 'peek-ahead' ||
      !showPrevButton ||
      !isSwiperReady ||
      !hasOnPrevClick
    )
      return;
    const prevEl = swiperRef.current?.navigation?.prevEl as HTMLElement | null | undefined;
    if (!prevEl) return;

    const apply = (): void => {
      if (prevEl instanceof HTMLButtonElement && prevEl.disabled) prevEl.disabled = false;
      if (prevEl.getAttribute('tabindex') !== '0') prevEl.setAttribute('tabindex', '0');
      if (prevEl.getAttribute('aria-disabled') !== 'false')
        prevEl.setAttribute('aria-disabled', 'false');
    };
    apply();
    const observer = new MutationObserver(apply);
    observer.observe(prevEl, {
      attributes: true,
      attributeFilter: ['disabled', 'tabindex', 'aria-disabled'],
    });
    return () => observer.disconnect();
  }, [navigationEnabled, variant, showPrevButton, isSwiperReady, hasOnPrevClick]);

  usePeekAheadNavigation({
    swiperRef,
    navigationEnabled,
    variant,
    allowSlideNext,
    isSwiperReady,
  });

  useKeepChevronsFocusable({
    swiperRef,
    navigationEnabled,
    isSwiperReady,
    chevronKeyboardNavRef,
  });

  // React to `hideInactiveSlidesFromA11y` transitions after mount; prev ref
  // avoids clobbering attributes we never set
  const prevHideInactiveRef = useRef(hideInactiveSlidesFromA11y);
  useEffect(() => {
    const wasEnabled = prevHideInactiveRef.current;
    prevHideInactiveRef.current = hideInactiveSlidesFromA11y;
    if (!isSwiperReady) return;
    const swiper = swiperRef.current;
    if (!swiper) return;
    if (!wasEnabled && hideInactiveSlidesFromA11y) {
      applyInactiveSlideA11y(swiper);
    } else if (wasEnabled && !hideInactiveSlidesFromA11y) {
      clearInactiveSlideA11y(swiper);
    }
  }, [hideInactiveSlidesFromA11y, isSwiperReady]);

  // Enter/Space synthesize a button click after focus overrides (boundary `disabled`
  // blocks programmatic click until re-enabled). Swiper's keydown is suppressed via
  // stopImmediatePropagation so navigation is not double-fired.
  useEffect(() => {
    if (!navigationEnabled || !isSwiperReady) return;
    const nav = swiperRef.current?.navigation;
    const prevEl = nav?.prevEl as HTMLElement | null | undefined;
    const nextEl = nav?.nextEl as HTMLElement | null | undefined;
    if (!prevEl && !nextEl) return;

    const onKey = (event: KeyboardEvent): void => {
      if (!isChevronActivationKey(event)) return;
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      const direction = resolveChevronKeyboardDirection(target, prevEl, nextEl);
      if (!direction) return;

      const swiper = swiperRef.current;
      if (!swiper) return;

      const chevronEl = direction === 'prev' ? prevEl : nextEl;
      if (!(chevronEl instanceof HTMLButtonElement)) return;

      applyChevronFocusOverrides(chevronEl);

      event.preventDefault();
      event.stopImmediatePropagation();

      const { loop } = swiper.params;
      const willSlide =
        direction === 'next' ||
        (direction === 'prev' && swiper.allowSlidePrev && !(swiper.isBeginning && !loop));

      let finishChevronKeyboardNav: (() => void) | undefined;
      if (willSlide) {
        finishChevronKeyboardNav = beginChevronKeyboardNav(
          swiper,
          chevronKeyboardNavRef,
          chevronNavFinishRef,
        );
      }

      const activeIndexBefore = swiper.activeIndex;
      chevronEl.click();

      if (
        finishChevronKeyboardNav &&
        !swiper.animating &&
        swiper.activeIndex === activeIndexBefore
      ) {
        finishChevronKeyboardNav();
      }
    };

    prevEl?.addEventListener('keydown', onKey, true);
    nextEl?.addEventListener('keydown', onKey, true);
    return () => {
      chevronNavFinishRef.current?.();
      chevronNavFinishRef.current = null;
      chevronKeyboardNavRef.current = false;
      prevEl?.removeEventListener('keydown', onKey, true);
      nextEl?.removeEventListener('keydown', onKey, true);
    };
  }, [navigationEnabled, isSwiperReady]);

  // Snap back to slide 0 once keyboard focus leaves the carousel (so the next Tab
  // cycle re-enters from the first card). Container-scoped so chevrons count.
  useEffect(() => {
    if (!isSwiperReady) return;
    const swiperEl = swiperRef.current?.el as HTMLElement | undefined;
    const container = swiperEl?.parentElement;
    if (
      !(container instanceof HTMLElement) ||
      !container.classList.contains('vds-carousel__container')
    ) {
      return;
    }

    const onContainerFocusOut = (event: FocusEvent): void => {
      const relatedNode = event.relatedTarget instanceof Node ? event.relatedTarget : null;
      const losingFocus = event.target;
      const snapGuardAtFocusOut = chevronKeyboardNavRef.current;

      const maybeSnap = (): void => {
        const swiper = swiperRef.current;
        if (!swiper || !swiperEl) return;
        if (chevronKeyboardNavRef.current || snapGuardAtFocusOut || swiper.animating) return;
        if (isFocusWithinCarousel(container, swiperEl, null, document.activeElement)) return;
        // Don't reset if user has actively navigated the carousel
        if (hasCarouselChangedSlidesRef.current) return;
        snapCarouselToFirstSlideIfNeeded(swiper);
      };

      requestAnimationFrame(() => {
        if (!swiperRef.current || !swiperEl) return;
        if (isFocusWithinCarousel(container, swiperEl, relatedNode, document.activeElement)) {
          return;
        }

        // Tab toward a boundary chevron can report relatedTarget null in the same frame.
        if (!relatedNode && losingFocus instanceof Node && swiperEl.contains(losingFocus)) {
          requestAnimationFrame(maybeSnap);
          return;
        }

        maybeSnap();
      });
    };

    container.addEventListener('focusout', onContainerFocusOut, true);
    return () => container.removeEventListener('focusout', onContainerFocusOut, true);
  }, [isSwiperReady]);

  const handleBlur = (e: React.FocusEvent<HTMLElement>): void => {
    if (onBlur) {
      onBlur(e);
    }
  };

  // Suppress interactive title-row content while the row is hidden — the wrapper is only faded
  // (opacity: 0), so a rendered action or title link would be invisible yet still keyboard-focusable.
  const titleAction = isTitleHidden ? undefined : action;
  const titleHref = isTitleHidden ? undefined : href;

  const slidesArray = Children.toArray(children);
  const slides = Children.map(slidesArray, (slide, idx) => {
    return (
      <SwiperSlide key={`slide_${idx}`} virtualIndex={idx}>
        {slide}
      </SwiperSlide>
    );
  });

  return (
    <>
      {title && (
        <>
          <div
            className={classNames('vds-carousel__title-small', {
              'vds-carousel__title--hidden': isTitleHidden,
            })}
            aria-hidden={isTitleHidden ? true : undefined}>
            <CarouselTitle
              title={title}
              isLive={isLive}
              numberShows={numberShows}
              className="vds-carousel__title"
              size="small"
              data-ci={titleDataCi ? `${titleDataCi}-small` : undefined}
              href={titleHref}
              linkComponent={titleLinkComponent}
              action={titleAction}
            />
          </div>
          <div
            className={classNames('vds-carousel__title-medium', {
              'vds-carousel__title--hidden': isTitleHidden,
            })}
            aria-hidden={isTitleHidden ? true : undefined}>
            <CarouselTitle
              title={title}
              isLive={isLive}
              numberShows={numberShows}
              className="vds-carousel__title"
              size="medium"
              data-ci={titleDataCi ? `${titleDataCi}-medium` : undefined}
              href={titleHref}
              linkComponent={titleLinkComponent}
              action={titleAction}
            />
          </div>
        </>
      )}
      <div
        ref={containerRef}
        className={classNames('vds-carousel__container', {
          'vds-carousel__container--nav-ready': !navigationEnabled || isNavReady,
          'vds-carousel__container--nav-inactive': navigationEnabled && isNavReady && isNavInactive,
          'vds-carousel__container--chevron-align-tile': chevronAlign === 'tile',
          'vds-carousel__container--chevron-align-bottom':
            chevronAlign === 'tile' && chevronAlignOrigin === 'bottom',
        })}>
        <Swiper
          id={id}
          className={classNames(className, 'vds-carousel', {
            [`vds-carousel--${variant}`]: variant !== 'default',
            'vds-carousel--show-prev-button': variant === 'peek-ahead' && showPrevButton,
          })}
          modules={[Autoplay, EffectFade, A11y, Navigation, Virtual]}
          navigation={
            navigationEnabled ? { prevEl: prevRef.current, nextEl: nextRef.current } : false
          }
          virtual={virtual}
          {...(!virtual || hideInactiveSlidesFromA11y ? { watchSlidesProgress: true } : {})}
          simulateTouch={false}
          spaceBetween={DEFAULT_SPACE_BETWEEN}
          onBeforeInit={handleBeforeInit}
          onSwiper={handleOnSwiper}
          onAfterInit={handleAfterInit}
          onSlideChange={handleSlideChange}
          onBlur={handleBlur}
          allowSlideNext={allowSlideNext}
          {...restSwiperProps}
          {...(mergedA11y !== undefined ? { a11y: mergedA11y } : {})}>
          {/* Swiper only reads `slot` on direct children, so the chevrons can't share a Fragment. */}
          {navigationEnabled && (
            <button
              {...prevButtonRest}
              slot="container-start"
              type="button"
              ref={prevRef}
              hidden={variant === 'peek-ahead' && !showPrevButton ? true : undefined}
              className={classNames(
                'vds-carousel__chevron',
                'vds-carousel__chevron--prev',
                prevButtonClassName,
              )}>
              <Icon className="vds-carousel__chevron-icon" name="chevron-backward" size="large" />
            </button>
          )}
          {navigationEnabled && (
            <button
              {...nextButtonRest}
              slot="container-start"
              type="button"
              ref={nextRef}
              className={classNames(
                'vds-carousel__chevron',
                'vds-carousel__chevron--next',
                nextButtonClassName,
              )}>
              <Icon className="vds-carousel__chevron-icon" name="chevron-forward" size="large" />
            </button>
          )}
          {slides}
        </Swiper>
      </div>
    </>
  );
};
